import {createRef, useCallback, useContext, useEffect, useRef} from 'react';
import omit from 'lodash/omit';
import cx from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {Canvas} from '~/common/canvas/Canvas';
import {Uyo} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/Uyo/Uyo';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {
  selectActiveCardEditor,
  selectFabricArt,
  selectCountOfUyoImages,
  selectIsPristineUyo,
  selectOriginalFabricArt,
  selectUsedPremiumFeature,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {SET_TEMPLATE_SNAPSHOT} from '~/CreateFabricInvitation/reducers/fabricTemplate/constants';
import {Envelope} from '~/CreateFabricInvitation/components/Envelope/Envelope';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {
  canvasAddTextBordersAndIcons,
  canvasHideEditIcon,
  canvasHideTextBorder,
  canvasRemoveEditIcon,
  canvasRemoveTextBordersAndIcons,
  canvasShowTextBordersAndIcons,
} from '~/common/canvas/customs/text_border';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';
import './CardTemplate.sass';

const removeElements = (objects) =>
  objects?.map((obj) => omit(obj, ['_originalText', '_originalObject', 'version', 'styles']));

export const CardTemplate = ({showActiveEditor}) => {
  const fabricArt = useSelector(selectFabricArt);
  const dispatch = useDispatch();
  const {canvas, setCanvas, setActiveObjects} = useContext(EventCreateContext);
  const originalFabricArt = useSelector(selectOriginalFabricArt);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const activeCardEditor = useSelector(selectActiveCardEditor);
  const isPristineUyo = useSelector(selectIsPristineUyo);
  const elementRef = useRef(null);
  // Hacky way to detect first time through, but title is empty on first creation,
  // and required to be set before coming back to this step.
  const firstTime = useSelector((state) => !state.details?.title);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;

  const uyoOriginalObjectsIds = originalFabricArt.objects
    .filter((object) => object.type === 'uYOImage')
    .map((obj) => deriveIdFromCanvasObject(obj));
  // TODO: temporary, needs to improve implementation <-- Seems not temporary
  window.canvas = canvas;

  const countOfUyoImages = useSelector(selectCountOfUyoImages);
  const isUyo = countOfUyoImages > 0;

  const saveSnapshot = useCallback((snapshot) => {
    dispatch({type: SET_TEMPLATE_SNAPSHOT, payload: snapshot});
  }, []);

  const uyoRefs = useRef([]);
  // Update the uyoRefs array to have the same length as uyoObjects
  uyoRefs.current = uyoOriginalObjectsIds.map((_, index) => uyoRefs.current[index] || createRef());

  const setActiveCanvasObject = (canvasInstance, checkForActiveTextbox, checkForEmptyImgSrc) => {
    const canvasObjects = canvasInstance?.getObjects();

    if (activeCardEditor === 'text' && checkForActiveTextbox) {
      const textbox = canvasObjects?.find(
        (obj) =>
          // Calculating if the textbox is actually on the canvas, 1350 is the (calculated) height of the canvas
          obj.type === 'textbox' && obj.height && obj.top < 1350 && obj.left > -(obj.width / 2)
      );

      if (textbox) {
        canvasHideTextBorder(textbox, canvasInstance);
        canvasHideEditIcon(textbox, canvasInstance);
        canvasInstance.setActiveObject(textbox);
        canvasInstance.renderAll();
      }
    } else if (
      activeCardEditor === 'image' &&
      !isPristineUyo &&
      canvasInstance?.getActiveObjects()?.length === 0
    ) {
      const uyoImage = canvasObjects?.find((obj) => obj.type === 'uYOImage');
      canvasShowTextBordersAndIcons(canvasInstance);
      if (checkForEmptyImgSrc) {
        if (uyoImage) {
          canvasInstance.setActiveObject(uyoImage);
          canvasInstance.renderAll();
        }
      }
    }
  };

  const onCanvasLoadHandler = useCallback(
    (canvasInstance) => {
      // Ensure no duplicate text borders/icons are visible when re-loading canvas
      canvasRemoveTextBordersAndIcons(canvasInstance);

      dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {loaded: true}});

      // Create dashed borders and edit icons for the textboxes showing they're editable
      canvasAddTextBordersAndIcons(canvasInstance, originalFabricArt).then(() => {
        const uyoImages = canvasInstance.getObjects().filter((obj) => obj.type === 'uYOImage');
        const uyoImage = uyoImages.find((obj) => obj.src !== '') || uyoImages[0];
        setActiveCanvasObject(canvasInstance, true, uyoImage?.src !== '');
      });
    },
    [firstTime]
  );

  useEffect(() => {
    const activeObjs = canvas?.getActiveObjects();
    const noActiveTextbox =
      !activeObjs || activeObjs.length === 0 || activeObjs[0]?.type !== 'textbox';
    setActiveCanvasObject(canvas, noActiveTextbox, true);
  }, [activeCardEditor]);

  const onChangeHandler = useCallback(
    (innerFabricArt) => {
      const objectTextFilter = (object) => object.type === 'textbox';
      const modifiedObjects = removeElements(innerFabricArt.objects.filter(objectTextFilter));
      const originalObjects = removeElements(originalFabricArt.objects.filter(objectTextFilter));

      const objectsMatchOriginals = () => {
        let objectsMatch = true;

        modifiedObjects.forEach((obj) => {
          const matchFound = originalObjects.find((o) => o.text === obj.text);
          if (!matchFound) {
            objectsMatch = false;
          }
        });

        return objectsMatch;
      };

      const pristine = objectsMatchOriginals();

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          fabricArt: innerFabricArt,
          pristine,
        },
      });
    },
    [originalFabricArt, dispatch]
  );

  useEffect(() => {
    const handleMouseDown = () => {
      canvasShowTextBordersAndIcons(canvas);
      canvas
        .getActiveObjects()
        .forEach((obj) => obj.type === 'textbox' && canvasHideTextBorder(obj, canvas));
    };

    const handleMouseUpOrObjectMoving = (event) => {
      canvas.getActiveObjects().forEach((activeObj) => {
        canvasHideTextBorder(activeObj, canvas);
        canvasHideEditIcon(activeObj, canvas);
      });

      const activeEditorMap = {
        textbox: 'text',
        uYOImage: 'image',
      };

      if (event?.e?.type === 'mouseup' && event?.target && !activeEditorMap[activeCardEditor]) {
        const payload = {};

        const activeEditor = activeEditorMap[event.target.type] || '';

        if (activeEditor !== '') {
          payload.activeCardEditor = activeEditor;
        }

        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload,
        });
      }
    };

    const handleObjectModified = (e) =>
      e.target?.type === 'textbox' && canvasRemoveEditIcon(e.target, canvas);

    canvas?.on('mouse:down', handleMouseDown);
    canvas?.on('mouse:up', handleMouseUpOrObjectMoving);
    canvas?.on('object:moving', handleMouseUpOrObjectMoving);
    canvas?.on('object:modified', handleObjectModified);

    return () => {
      canvas?.off('mouse:down', handleMouseDown);
      canvas?.off('mouse:up', handleMouseUpOrObjectMoving);
      canvas?.off('object:moving', handleMouseUpOrObjectMoving);
      canvas?.off('object:modified', handleObjectModified);
    };
  }, [canvas]);

  const setActiveObjectsHandler = useCallback(
    (activeObjects) => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          editorDisabled: !activeObjects,
        },
      });

      setActiveObjects(activeObjects);
    },
    [dispatch]
  );

  const updateActiveCardEditorHandler = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        activeCardEditor: 'intro',
      },
    });
  }, [dispatch]);

  return (
    <div
      className={cx('card-template', {
        'card-template--no-editor': !showActiveEditor,
        'card-template--uyo': isUyo,
      })}
      ref={elementRef}
    >
      <div className="card-template__images">
        <Envelope
          type={isMobile ? 'blank' : 'flap_down_image'}
          className="card-template__envelope"
          redirectTo={isMobile ? null : Sections.ENVELOPE_LINER}
          forceShowEnvelope={usedPremiumFeature}
        />
        {uyoOriginalObjectsIds.length > 0
          ? uyoOriginalObjectsIds?.map((uyoId, index) => (
              <Uyo
                btnText="Upload Photo"
                nodeRef={uyoRefs.current[index]}
                index={index}
                id={uyoId}
                key={uyoId}
              />
            ))
          : null}
        <Canvas
          configJson={fabricArt}
          onChange={onChangeHandler}
          setCanvas={setCanvas}
          setActiveObjects={setActiveObjectsHandler}
          saveSnapshot={saveSnapshot}
          onLoad={onCanvasLoadHandler}
          className="fabric_user_card card-template__canvas_mobile"
          parentNode={`${isMobile ? 'create-container' : ''}`}
          callback={updateActiveCardEditorHandler}
        />
      </div>
    </div>
  );
};
